"use client";

import { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Container } from "react-bootstrap";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useMutation } from "@tanstack/react-query";
import { loginBg, logoSmall } from "@/utils/image";
import { forgotPassword } from "@/utils/api";
import { validationPatterns } from "@/utils/validations";
import { useAuthStore } from "@/store/useAuthStore";
import MasterButton from "@/components/common/masterButton";
import useToast from "@/hooks/useToast";
import SmartEmailPhoneInput from "@/components/auth/SmartEmailPhoneInput";
import { isValidPhoneNumber } from "libphonenumber-js";

const initialValues = {
  emailOrPhone: "",
};

const validationSchema = Yup.object({
  emailOrPhone: Yup.string()
    .trim()
    .required("Email or phone number is required")
    .test("email-or-phone", "Invalid email or phone number", function (value) {
      if (!value) return false;

      // Check if it's an email
      if (value.includes("@")) {
        return validationPatterns.email.test(value);
      }

      // Check if it's a valid phone number
      try {
        return isValidPhoneNumber(value);
      } catch {
        return false;
      }
    }),
});

export default function ForgotPassword() {
  const router = useRouter();
  const { setEmail, clearEmail, setOtpFlow, clearOtpFlow } = useAuthStore();
  const { showToast } = useToast();

  // Clear any existing email/flow from previous sessions
  useEffect(() => {
    clearEmail();
    clearOtpFlow();
  }, [clearEmail, clearOtpFlow]);

  // Initialize formik
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const { emailOrPhone } = values;

      // Determine if it's email or phone
      const payload = emailOrPhone.includes("@")
        ? { email: emailOrPhone }
        : { phone: emailOrPhone };

      mutate(payload);
    },
  });

  const { handleSubmit, handleChange, values, errors, touched } = formik;

  // Mutation for forgot password
  const { mutate, isPending } = useMutation({
    mutationFn: (values) => forgotPassword(values),
    onSuccess: (data, variables) => {
      if (data?.success) {
        // Store email/phone and flow type in Zustand
        const identifier = variables.email || variables.phone;
        setEmail(identifier);
        setOtpFlow("forgot-password");

        router.push("/otp");

        showToast({
          message: data?.message || "Reset code sent successfully!",
          type: "success",
        });
      }
    }
  });

  return (
    <div className="position-relative commonAuthOuter">
      <div className="d-flex justify-content-center m_bottom39">
        <Link href="/">
          <Image src={logoSmall} alt="logo" />
        </Link>
      </div>

      <Container
        fluid
        className="login_Wrapper d-flex justify-content-center align-items-start"
      >
        <div
          className="login_Card"
          style={{ maxWidth: "486px", width: "100%" }}
        >
          <h3 className="text-center mb-4 heading24 black fw-bold">
            Reset your password
          </h3>

          <form onSubmit={handleSubmit}>
            {/* Smart Email/Phone Field */}
            <div className="position-relative mb-4">
              <SmartEmailPhoneInput
                value={values.emailOrPhone}
                onChange={(value) =>
                  formik.setFieldValue("emailOrPhone", value)
                }
                error={touched.emailOrPhone && errors.emailOrPhone}
                placeholder="Email or phone no"
                label="Email or Phone no"
              />
            </div>

            {/* Submit Button */}
            <MasterButton
              disabled={isPending}
              buttonName={isPending ? "Sending..." : "Get OTP"}
              type="submit"
              width="w-100"
            />
          </form>
        </div>
      </Container>

      {/* <Image
        className="login_bg"
        src={loginBg}
        alt="landing-bg"
        width={100}
        height={400}
      /> */}
    </div>
  );
}
